<template>
  <div class="auth-page">
    <navbar />

    <main class="auth-main">
      <section class="auth-panel" aria-live="polite">
        <div class="auth-panel-topline"></div>

        <div class="auth-kicker">GixCore Account</div>
        <h1 class="auth-title">{{ pageTitle }}</h1>
        <p class="auth-copy">{{ pageCopy }}</p>

        <!-- Validating the token -->
        <div v-if="phase === 'validating'" class="auth-message" role="status">
          <span class="auth-message-title">Checking your link…</span>
          <span>One moment while we validate your reset link.</span>
        </div>

        <!-- Token invalid / expired -->
        <div v-else-if="phase === 'invalid'" class="auth-message is-warning" role="status">
          <span class="auth-message-title">This link is invalid or has expired.</span>
          <span>{{ invalidMessage }}</span>
        </div>

        <!-- Success -->
        <div v-else-if="phase === 'done'" class="auth-message is-success" role="status">
          <span class="auth-message-title">{{ isCreate ? 'Password created.' : 'Password updated.' }}</span>
          <span>You can sign in with your email and new password now.</span>
        </div>

        <!-- Reset form -->
        <form v-else class="auth-form" v-on:submit.prevent="submit">
          <div v-if="email" class="auth-field">
            <label for="reset-email">Email address</label>
            <input id="reset-email" class="auth-input" type="email" :value="email" disabled>
          </div>

          <div class="auth-field">
            <label for="reset-password">New password</label>
            <input
              id="reset-password"
              v-model="password"
              class="auth-input"
              :class="{ 'is-invalid': hasFieldError('password') }"
              type="password"
              autocomplete="new-password"
              placeholder="at least 8 characters"
              @input="clearFieldError('password')"
              @blur="markTouched('password')"
              required
            >
            <span v-if="fieldMessage('password')" class="auth-field-error">{{ fieldMessage('password') }}</span>
          </div>

          <div class="auth-field">
            <label for="reset-password-confirm">Confirm password</label>
            <input
              id="reset-password-confirm"
              v-model="password_confirmation"
              class="auth-input"
              :class="{ 'is-invalid': hasFieldError('password_confirmation') }"
              type="password"
              autocomplete="new-password"
              placeholder="confirm password"
              @input="clearFieldError('password_confirmation')"
              @blur="markTouched('password_confirmation')"
              required
            >
            <span v-if="fieldMessage('password_confirmation')" class="auth-field-error">{{ fieldMessage('password_confirmation') }}</span>
          </div>

          <div v-if="authError" class="auth-error" role="alert">
            <span class="auth-error-title">{{ authError }}</span>
          </div>

          <div class="auth-actions">
            <button class="auth-submit" :class="{ 'is-loading': loading }" type="submit" :disabled="loading || !formValid">
              {{ submitLabel }}
            </button>
          </div>
        </form>

        <div class="auth-links is-login">
          <router-link class="auth-link-strong" to="/users/login">Back to sign in</router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import navbar from '../navbar.vue'

export default {
  name: 'ResetPassword',
  components: { navbar },
  data() {
    return {
      phase: 'validating', // validating | invalid | ready | done
      loading: false,
      authError: '',
      invalidMessage: 'Request a new link and open the most recent email in your inbox.',
      email: '',
      isCreate: false,
      password: '',
      password_confirmation: '',
      submitted: false,
      touched: { password: false, password_confirmation: false },
    }
  },
  computed: {
    token() {
      return (this.$route.query && this.$route.query.token) || ''
    },
    pageTitle() {
      if (this.phase === 'done') return 'All set'
      if (this.phase === 'invalid') return 'Link expired'
      return this.isCreate ? 'Create your password' : 'Reset your password'
    },
    pageCopy() {
      if (this.phase === 'validating') return 'Validating your reset link.'
      if (this.phase === 'invalid') return 'Reset links are single-use and time-limited.'
      if (this.phase === 'done') return 'Your password is ready to use.'
      return this.isCreate
        ? 'Set a password so you can sign in with email as well as Google.'
        : 'Choose a new password for your account.'
    },
    submitLabel() {
      if (this.loading) return 'Saving…'
      return this.isCreate ? 'Create Password' : 'Reset Password'
    },
    validationErrors() {
      const errors = {}
      if (!this.password) {
        errors.password = 'Password is required.'
      } else if (this.password.length < 8) {
        errors.password = 'Use at least 8 characters.'
      }
      if (!this.password_confirmation) {
        errors.password_confirmation = 'Confirm your password.'
      } else if (this.password && this.password_confirmation !== this.password) {
        errors.password_confirmation = 'Passwords do not match.'
      }
      return errors
    },
    formValid() {
      return Object.keys(this.validationErrors).length === 0
    },
  },
  mounted() {
    this.validateToken()
  },
  methods: {
    async validateToken() {
      if (!this.token) {
        this.phase = 'invalid'
        this.invalidMessage = 'This link is missing its token. Request a new email and try again.'
        return
      }
      this.phase = 'validating'
      try {
        const response = await this.$store.dispatch('validateResetToken', this.token)
        const data = (response && response.data) || {}
        this.email = data.email || ''
        // Google-only accounts are "creating" a password rather than resetting one.
        const hasPassword = data.has_password
        this.isCreate = hasPassword === false || data.mode === 'create' ||
          String(data.auth_provider || '').toLowerCase() === 'google'
        this.phase = 'ready'
      } catch (err) {
        this.phase = 'invalid'
        const msg = this.extractError(err)
        if (msg) this.invalidMessage = msg
      }
    },
    async submit() {
      this.submitted = true
      this.touched.password = true
      this.touched.password_confirmation = true
      if (!this.formValid) {
        this.authError = 'Check the highlighted fields before continuing.'
        return
      }
      this.loading = true
      this.authError = ''
      try {
        await this.$store.dispatch('resetPassword', {
          token: this.token,
          password: this.password,
          password_confirmation: this.password_confirmation,
        })
        this.phase = 'done'
      } catch (err) {
        this.authError = this.extractError(err) || 'We could not update your password. The link may have expired — request a new one.'
      } finally {
        this.loading = false
      }
    },
    extractError(err) {
      const data = err && err.response && err.response.data
      if (!data) return err && err.message ? err.message : ''
      if (typeof data.error === 'string') return data.error
      return data.message || (data.error && data.error.message) || ''
    },
    hasFieldError(field) {
      return !!this.fieldMessage(field)
    },
    markTouched(field) {
      this.$set(this.touched, field, true)
    },
    clearFieldError() {
      if (this.authError) this.authError = ''
    },
    fieldMessage(field) {
      if (!this.submitted && !this.touched[field]) return ''
      return this.validationErrors[field] || ''
    },
  },
}
</script>

<style scoped>
.auth-page {
	position: relative;
	min-height: 100vh;
	overflow: hidden;
	background: #080a0f;
	color: #cfd2da;
}

.auth-page::before {
	position: fixed;
	inset: 0;
	z-index: 0;
	background:
		linear-gradient(90deg, rgba(4, 6, 10, 0.86) 0%, rgba(6, 8, 12, 0.74) 46%, rgba(4, 6, 10, 0.92) 100%),
		linear-gradient(180deg, rgba(8, 10, 15, 0.2) 0%, rgba(8, 10, 15, 0.92) 100%),
		url('~@/assets/img/builder-bg.png');
	background-position: center;
	background-repeat: no-repeat;
	background-size: cover;
	content: "";
}

.auth-main {
	position: relative;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: calc(100vh - 72px);
	padding: 1rem 1.25rem 3rem;
}

.auth-panel {
	position: relative;
	width: min(100%, 420px);
	padding: 1.25rem;
	border: 1px solid rgba(67, 72, 87, 0.95);
	border-radius: 6px;
	background:
		linear-gradient(180deg, rgba(25, 27, 32, 0.94), rgba(13, 15, 22, 0.96)),
		rgba(25, 27, 32, 0.94);
	box-shadow:
		0 22px 70px rgba(0, 0, 0, 0.42),
		inset 0 0 0 1px rgba(255, 255, 255, 0.025);
}

.auth-panel-topline {
	position: absolute;
	top: -1px;
	right: 12px;
	left: 12px;
	height: 1px;
	background: linear-gradient(90deg, rgba(25, 151, 198, 0), rgba(25, 151, 198, 0.85), rgba(25, 151, 198, 0));
}

.auth-kicker {
	margin-bottom: 0.35rem;
	color: #1997c6;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.9px;
	line-height: 1;
	text-transform: uppercase;
}

.auth-title {
	margin: 0;
	color: #cfd2da;
	font-size: 25px;
	font-weight: 500;
	line-height: 1.18;
}

.auth-copy {
	max-width: 340px;
	margin: 0.55rem 0 1rem;
	color: #9198aa;
	font-size: 14px;
	line-height: 1.45;
}

.auth-message {
	display: grid;
	gap: 0.2rem;
	margin: 0 0 1rem;
	padding: 0.75rem 0.85rem;
	border: 1px solid rgba(25, 151, 198, 0.48);
	border-radius: 6px;
	background: rgba(25, 151, 198, 0.08);
	color: #bcd5df;
	font-size: 13px;
	line-height: 1.4;
}

.auth-message-title {
	color: #dceaf0;
	font-weight: 800;
}

.auth-message.is-success {
	border-color: rgba(0, 188, 140, 0.5);
	background: rgba(0, 188, 140, 0.08);
	color: #bfe9df;
}

.auth-message.is-success .auth-message-title {
	color: #9ff0d8;
}

.auth-message.is-warning {
	border-color: rgba(231, 195, 82, 0.5);
	background: rgba(231, 195, 82, 0.08);
	color: #e7d79e;
}

.auth-message.is-warning .auth-message-title {
	color: #f1dc83;
}

.auth-form {
	display: grid;
	gap: 0.7rem;
}

.auth-field {
	display: grid;
	gap: 0.3rem;
}

.auth-field label {
	margin: 0;
	color: #b2b3b4;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.2px;
	line-height: 1;
}

.auth-input {
	width: 100%;
	min-height: 39px;
	padding: 0.55rem 0.72rem;
	border: 1px solid rgba(67, 72, 87, 0.96);
	border-radius: 6px;
	outline: none;
	background: rgba(6, 8, 12, 0.78);
	color: #e5e8ef;
	font-size: 15px;
	transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.auth-input::placeholder {
	color: rgba(145, 152, 170, 0.58);
}

.auth-input:hover {
	border-color: rgba(25, 151, 198, 0.55);
}

.auth-input:focus {
	border-color: #1997c6;
	background: rgba(8, 10, 15, 0.98);
	box-shadow: 0 0 0 3px rgba(25, 151, 198, 0.11);
}

.auth-input:disabled {
	color: #9198aa;
	cursor: not-allowed;
}

.auth-input.is-invalid {
	border-color: rgba(230, 71, 89, 0.9);
	box-shadow: 0 0 0 3px rgba(230, 71, 89, 0.1);
}

.auth-field-error {
	color: #f08a95;
	font-size: 11.5px;
	font-weight: 650;
	line-height: 1.3;
}

.auth-error {
	display: grid;
	gap: 0.25rem;
	padding: 0.75rem 0.85rem;
	border: 1px solid rgba(230, 71, 89, 0.55);
	border-radius: 6px;
	background: rgba(230, 71, 89, 0.09);
	color: #f08a95;
	font-size: 13px;
	line-height: 1.4;
}

.auth-error-title {
	color: #ff9aa5;
	font-weight: 700;
}

.auth-actions {
	margin-top: 0.1rem;
}

.auth-submit {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-height: 39px;
	border: 1px solid #16b4ed;
	border-radius: 6px;
	background: linear-gradient(180deg, rgba(25, 151, 198, 0.34), rgba(12, 66, 88, 0.64));
	box-shadow:
		0 0 24px rgba(25, 151, 198, 0.13),
		inset 0 0 18px rgba(25, 151, 198, 0.12);
	color: #f4f8fb;
	cursor: pointer;
	font-size: 13px;
	font-weight: 800;
	letter-spacing: 0.6px;
	line-height: 1;
	text-transform: uppercase;
	transition: background 160ms ease, box-shadow 160ms ease, color 160ms ease, transform 160ms ease;
}

.auth-submit:hover,
.auth-submit:focus {
	background: linear-gradient(180deg, rgba(25, 151, 198, 0.48), rgba(13, 88, 117, 0.78));
	box-shadow:
		0 0 30px rgba(25, 151, 198, 0.22),
		inset 0 0 18px rgba(25, 151, 198, 0.18);
	color: #ffffff;
}

.auth-submit:active {
	transform: translateY(1px);
}

.auth-submit:disabled {
	border-color: rgba(67, 72, 87, 0.96);
	background: rgba(67, 72, 87, 0.35);
	box-shadow: none;
	color: #9198aa;
	cursor: not-allowed;
	transform: none;
}

.auth-links {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: center;
	gap: 0.65rem;
	margin-top: 0.85rem;
	padding-top: 0.78rem;
	border-top: 1px solid rgba(67, 72, 87, 0.48);
}

.auth-link-strong {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-height: 30px;
	padding: 0 0.7rem;
	border: 1px solid rgba(25, 151, 198, 0.66);
	border-radius: 5px;
	background: rgba(8, 20, 30, 0.4);
	color: #16b4ed !important;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.45px;
	text-transform: uppercase;
	text-decoration: none;
}

.auth-link-strong:hover,
.auth-link-strong:focus {
	border-color: rgba(25, 151, 198, 0.95);
	background: rgba(25, 151, 198, 0.13);
	color: #dff8ff !important;
	text-decoration: none;
}

@media screen and (max-width: 640px) {
	.auth-main {
		align-items: stretch;
		padding: 0.6rem 1rem 1rem;
	}

	.auth-panel {
		width: 100%;
		padding: 1rem;
	}

	.auth-title {
		font-size: 23px;
	}
}
</style>
